/**
 * Decal parser: parses Decal TSCN properties into a typed shape, with Godot's defaults: size
 * (2, 2, 2), white opaque modulate, albedo_mix and emission_energy 1, normal_fade 0, upper and
 * lower fade 0.3, cull_mask 0xFFFFF (the 20 editor-visible layers), and distance fade off at
 * begin 40 and length 10, and sorting_offset 0. Texture references are set only when present.
 */
import { parseNode3D } from '../../base/node3d/parser';
import { parseColor } from '../../../utils/colorParser';
import { parseVector3 } from '../../../parser/vectors';
import { boolOr, floatOr, intOr } from '../../../parser/valueParsers';
import { warn } from '../../../logger';
import { GODOT_EDITOR_LAYERS_MASK } from '../../../godot/scene/visualLayers.js';
/** Godot Decal default box size. */
const DEFAULT_SIZE = { x: 2, y: 2, z: 2 };
/** Godot Decal default `cull_mask`. */
const DEFAULT_CULL_MASK = GODOT_EDITOR_LAYERS_MASK;
export function parseDecal(heading, properties) {
    const baseProperties = parseNode3D(heading, properties);
    let size = { ...DEFAULT_SIZE };
    if (properties.size) {
        try {
            size = parseVector3(properties.size);
        }
        catch (error) {
            warn(`[Decal] Failed to parse size: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    const result = {
        ...baseProperties,
        size,
        modulate: properties.modulate ? parseColor(properties.modulate) : { r: 1, g: 1, b: 1, a: 1 },
        albedo_mix: floatOr(properties.albedo_mix, 1),
        emission_energy: floatOr(properties.emission_energy, 1),
        normal_fade: floatOr(properties.normal_fade, 0),
        // Godot clamps both exponents at the setter (`Decal::set_upper_fade` /
        // `set_lower_fade`), so a negative authored value means zero, not a
        // NaN-producing `pow` base later.
        upper_fade: Math.max(0, floatOr(properties.upper_fade, 0.3)),
        lower_fade: Math.max(0, floatOr(properties.lower_fade, 0.3)),
        cull_mask: intOr(properties.cull_mask, DEFAULT_CULL_MASK, 'Decal.cull_mask', 'uint32'),
        distance_fade_enabled: boolOr(properties.distance_fade_enabled, false),
        distance_fade_begin: floatOr(properties.distance_fade_begin, 40),
        distance_fade_length: floatOr(properties.distance_fade_length, 10),
        sorting_offset: floatOr(properties.sorting_offset, 0),
    };
    if (properties.texture_albedo)
        result.texture_albedo = properties.texture_albedo;
    if (properties.texture_normal)
        result.texture_normal = properties.texture_normal;
    if (properties.texture_orm)
        result.texture_orm = properties.texture_orm;
    if (properties.texture_emission)
        result.texture_emission = properties.texture_emission;
    return result;
}
//# sourceMappingURL=parser.js.map